<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  formatLeadCurrency,
  taskColor,
  taskDotClass,
  waitingSince,
  WAITING_TONE_CLASSES,
} from '../helper';

const props = defineProps({
  lead: { type: Object, required: true },
  // En móvil (sin drag) se muestra un botón "Mover de etapa" explícito.
  movable: { type: Boolean, default: false },
  // Triage de la bandeja de entrada (estilo Kommo): ✓ aceptar (mover a la
  // primera etapa activa) y archivar, directo en la card.
  triage: { type: Boolean, default: false },
});

const emit = defineEmits(['open', 'edit', 'move', 'accept', 'archive']);

const { t } = useI18n();

// Distingue un click real de un drag: tras arrastrar la tarjeta el navegador
// dispara un `click`; solo emitimos 'edit' si el puntero casi no se movió.
let pointerDown = { x: 0, y: 0 };

const onPointerDown = event => {
  pointerDown = { x: event.clientX, y: event.clientY };
};

const onClick = event => {
  const moved =
    Math.abs(event.clientX - pointerDown.x) +
    Math.abs(event.clientY - pointerDown.y);
  if (moved <= 6) emit('edit', props.lead);
};

const conversationLabel = computed(() =>
  props.lead.conversation ? `#${props.lead.conversation.display_id}` : ''
);

const formattedValue = computed(() => {
  const amount = Number(props.lead.value);
  if (!amount) return null;
  return formatLeadCurrency(amount, props.lead.currency);
});

// Reloj de espera (solo bandeja): presión honesta para vaciar Entrantes.
const waiting = computed(() =>
  props.triage ? waitingSince(props.lead.created_at) : null
);
const waitingClass = computed(() =>
  waiting.value ? WAITING_TONE_CLASSES[waiting.value.tone] : ''
);
const waitingLabel = computed(() => {
  if (!waiting.value) return '';
  if (waiting.value.unit === 'now') return t('LEADS.TRIAGE.WAITING_NOW');
  if (waiting.value.unit === 'hours')
    return t('LEADS.TRIAGE.WAITING_HOURS', { n: waiting.value.n });
  return t('LEADS.TRIAGE.WAITING_DAYS', { n: waiting.value.n });
});

const dotClass = computed(() => taskDotClass(props.lead.next_task_at));
const dotTitle = computed(() => {
  const color = taskColor(props.lead.next_task_at);
  if (color === 'ruby') return t('LEADS.TASK.OVERDUE');
  if (color === 'teal') return t('LEADS.TASK.TODAY');
  return '';
});
</script>

<template>
  <!-- Bandeja (triage): la card se ve CRUDA — borde punteado, sin sombra —
       para gritar "esto no está procesado" (estilo Kommo). -->
  <div
    class="flex flex-col gap-2 p-3 border rounded-lg cursor-pointer bg-n-solid-1"
    :class="
      triage
        ? 'border-dashed border-n-slate-7 hover:border-n-slate-9'
        : 'shadow-sm border-n-weak hover:border-n-slate-6'
    "
    @pointerdown="onPointerDown"
    @click="onClick"
  >
    <div class="inline-flex items-center gap-1">
      <span class="text-sm font-medium text-n-slate-12 line-clamp-2">
        {{ lead.name }}
      </span>
      <span
        v-if="dotClass"
        class="size-2 rounded-full shrink-0"
        :class="dotClass"
        :title="dotTitle"
      />
      <span
        v-if="waiting"
        class="inline-flex items-center gap-1 px-1.5 py-0.5 ml-auto text-[11px] font-medium rounded-full shrink-0"
        :class="waitingClass"
        :title="t('LEADS.TRIAGE.WAITING_TITLE')"
      >
        <span class="i-lucide-clock size-3" aria-hidden="true" />
        {{ waitingLabel }}
      </span>
    </div>
    <span v-if="formattedValue" class="text-xs font-semibold text-n-teal-11">
      {{ formattedValue }}
    </span>
    <div class="flex items-center justify-between gap-2 mt-0.5">
      <Avatar
        v-if="lead.assignee"
        :name="lead.assignee.name"
        :src="lead.assignee.thumbnail || ''"
        :size="20"
      />
      <span v-else class="text-xs text-n-slate-10">{{
        t('LEADS.UNASSIGNED')
      }}</span>
      <!-- En móvil (movable) los botones suben a sm: target táctil digno para
           la acción core de mover; en desktop xs preserva la densidad del Kanban.
           El variant link usa p-0 en TODOS los tamaños (solo crece la fuente),
           así que en móvil se cambia a faded para que el área táctil sea real. -->
      <div class="flex flex-wrap items-center justify-end gap-2">
        <Button
          v-if="lead.conversation && !triage"
          :variant="movable ? 'faded' : 'link'"
          :color="movable ? 'slate' : 'blue'"
          :size="movable ? 'sm' : 'xs'"
          icon="i-lucide-message-circle"
          :label="conversationLabel"
          @click.stop="emit('open', lead)"
        />
        <Button
          v-if="triage"
          ghost
          color="slate"
          :size="movable ? 'sm' : 'xs'"
          icon="i-lucide-archive"
          :title="t('LEADS.ARCHIVE.ARCHIVE')"
          @click.stop="emit('archive', lead)"
        />
        <Button
          v-if="movable"
          faded
          color="slate"
          size="sm"
          icon="i-lucide-arrow-right-left"
          :label="t('LEADS.MOVE')"
          @click.stop="emit('move', lead)"
        />
      </div>
    </div>
    <!-- CTA de la bandeja: el trabajo real es RESPONDER al cliente (con el
         auto-aceptar, responder ES aceptar). El ✓ queda como vía manual chica
         (calificar sin responder / negocios con el knob apagado). Leads sin
         conversación: la única vía es aceptar a mano → ese es su CTA. -->
    <Button
      v-if="triage && lead.conversation"
      color="blue"
      size="sm"
      class="w-full"
      icon="i-lucide-message-circle"
      :label="t('LEADS.TRIAGE.REPLY')"
      :title="t('LEADS.TRIAGE.REPLY_TITLE')"
      @click.stop="emit('open', lead)"
    />
    <template v-else-if="triage">
      <!-- Lead manual sin conversación: no hay a quién responder — la etiqueta
           explica por qué esta card difiere de las demás de la bandeja. -->
      <p class="text-[11px] text-n-slate-10">
        {{ t('LEADS.TRIAGE.NO_CONVERSATION') }}
      </p>
      <Button
        faded
        color="teal"
        size="sm"
        class="w-full"
        icon="i-lucide-check"
        :label="t('LEADS.TRIAGE.ACCEPT')"
        :title="t('LEADS.TRIAGE.ACCEPT_TITLE')"
        @click.stop="emit('accept', lead)"
      />
    </template>
  </div>
</template>
